import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  fetchDueTodayCongratulationStatus,
  scoopProjectBadgeForTabs,
} from "../redux/Thunk/DashboardModule/DashboardThunk";
import { isDueTodayCongratulationsDisabled } from "../Helper/dueTodayCongratulationsPreference";
import DueTodayCongratulationsDialog from "./DueTodayCongratulationsDialog";

const PUBLIC_AUTH_PATH_PREFIXES = [
  "/login",
  "/forgot-password",
  "/signup",
  "/successfulRegistration",
  "/resetpassword",
  "/activation",
  "/job-accept",
];

const isPublicAuthPath = (pathname) =>
  PUBLIC_AUTH_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const DueTodayCongratulations = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const authUser = useSelector((state) => state?.root?.authUser);
  const user = authUser?.user;
  const isAuthenticated = Boolean(authUser?.isAuthenticated && localStorage.getItem("token"));
  const isInternalUser = Number(user?.iFkUserTypeId) === 1;

  const [congratulationsOpen, setCongratulationsOpen] = useState(false);
  const pollingTimeoutRef = useRef(null);
  const abortedRef = useRef(false);
  const lastVersionRef = useRef(null);
  const prevAllCompletedRef = useRef(false);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    abortedRef.current = false;

    const clearPolling = () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };

    const shouldPoll =
      isAuthenticated &&
      isInternalUser &&
      !isPublicAuthPath(location.pathname);

    if (!shouldPoll) {
      clearPolling();
      lastVersionRef.current = null;
      prevAllCompletedRef.current = false;
      hasInitializedRef.current = false;
      return () => {
        abortedRef.current = true;
        clearPolling();
      };
    }

    const processStatus = (payload) => {
      if (!payload || payload.enabled === false) {
        return;
      }

      const version =
        payload.version !== undefined && payload.version !== null
          ? Number(payload.version)
          : null;
      const allCompleted = Boolean(payload.all_completed);

      if (
        version !== null &&
        lastVersionRef.current !== null &&
        version !== lastVersionRef.current &&
        user?.iUserId
      ) {
        dispatch(
          scoopProjectBadgeForTabs({ userId: user.iUserId, silent: true })
        );
      }

      if (version !== null) {
        lastVersionRef.current = version;
      }

      if (hasInitializedRef.current) {
        if (
          !prevAllCompletedRef.current &&
          allCompleted &&
          !isDueTodayCongratulationsDisabled()
        ) {
          setCongratulationsOpen(true);
        }
      } else {
        hasInitializedRef.current = true;
      }

      prevAllCompletedRef.current = allCompleted;
    };

    const pollStatus = async () => {
      if (abortedRef.current || document.visibilityState === "hidden") {
        return;
      }

      try {
        const result = await dispatch(fetchDueTodayCongratulationStatus()).unwrap();
        processStatus(result);
      } catch (err) {
        console.error("Due today congratulation poll failed", err);
      }
    };

    const schedulePolling = () => {
      if (abortedRef.current) return;
      if (document.visibilityState === "hidden") return;

      const baseMs = 300000; // 5 minutes
      const jitterMs = Math.floor(baseMs * 0.1 * (Math.random() * 2 - 1));
      const nextMs = Math.max(15000, baseMs + jitterMs);

      pollingTimeoutRef.current = setTimeout(async () => {
        await pollStatus();
        schedulePolling();
      }, nextMs);
    };

    const onVisibilityChange = () => {
      if (abortedRef.current) return;
      clearPolling();
      if (document.visibilityState === "hidden") {
        return;
      }
      pollStatus();
      schedulePolling();
    };

    pollStatus();
    schedulePolling();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      abortedRef.current = true;
      clearPolling();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [
    dispatch,
    isAuthenticated,
    isInternalUser,
    location.pathname,
    user?.iUserId,
  ]);

  return (
    <DueTodayCongratulationsDialog
      open={congratulationsOpen}
      onClose={() => setCongratulationsOpen(false)}
    />
  );
};

export default DueTodayCongratulations;
